import React, { Component } from 'react';
import { formatEventDateTime } from '../utils/formats';
import { describeInclusion, menuByCodeOf } from '../utils/eventIncluded';
import EventInclusionsEditor, { rowsFromInclusions, inclusionsPayload, inclusionsError } from './EventInclusionsEditor';

var config = process.env;

const KIND_OPTIONS = [
    { value: 'general', label: 'General' },
    { value: 'early_bird', label: 'Early bird' },
    { value: 'member', label: 'Socio' },
    { value: 'free', label: 'Gratis' },
];

// Panel de tipos de entrada de un evento: lista los existentes (recibidos por props),
// permite EDITARLOS (PATCH) y dar de alta nuevos (POST). Cada tipo lleva además su "Qué incluye"
// (spec-eventos-consumo-incluido): los renglones se editan en el mismo formulario y viajan en
// el mismo pedido.
class EventTicketTypesComponent extends Component {
    constructor(props) {
        super(props);
        // El menú de cafetería (para elegir ítems y para nombrarlos en la tabla) no es del
        // formulario: no se vacía al guardar ni al cancelar. null = todavía no se pidió o no llegó.
        //
        // `saved` tampoco: es lo que el servidor devolvió al guardar cada tipo (por id), y manda
        // sobre `props.ticketTypes` hasta que un refresco posterior a ese guardado llegue bien.
        // El guardado reemplaza la lista entera de "Qué incluye" (FR-003): si el formulario
        // partiera de props viejas porque el refresco falló o todavía no volvió, volver a editar
        // borraría lo que se acaba de guardar.
        this.state = { ...this.emptyForm(), menu: null, menuError: false, saved: {} };
        this.saveSeq = 0;
    }

    emptyForm() {
        return {
            editingId: null, name: '', price: '', kind: 'general', validFrom: '', validUntil: '', active: true, disabled: false,
            // Renglones de "Qué incluye" y si hay que mandarlos: SÓLO si se tocó el editor. Sin la
            // clave el backend no toca lo declarado (FR-004), así que cambiar el precio de un tipo
            // no reenvía una lista que nadie editó.
            inclusions: [], sendInclusions: false,
            // El tipo en edición llegó con `inclusions: null`: el servidor no pudo leer qué
            // incluye. No es "no incluye nada": el editor no se ofrece y la lista NUNCA viaja.
            inclusionsUnreadable: false,
        };
    }

    // Los tipos de entrada como hay que mostrarlos y editarlos: los de props, con lo último que
    // se guardó desde acá encima, más los recién creados que props todavía no trae.
    ticketTypes = () => {
        const fromProps = this.props.ticketTypes || [];
        const saved = this.state.saved || {};
        const merged = fromProps.map((tt) => (saved[tt.id] ? { ...tt, ...saved[tt.id].ticketType } : tt));
        const known = new Set(fromProps.map((tt) => String(tt.id)));
        Object.keys(saved).forEach((id) => {
            if (!known.has(String(id))) merged.push(saved[id].ticketType);
        });
        return merged;
    }

    // Lo que devolvió el guardado, listo para mostrar. `inclusions` queda en null ("no se pudo
    // leer") si la respuesta lo dice o si no cierra con lo que se mandó: la lectura de después
    // del guardado es best-effort en el backend, y una lista vacía donde tenía que haber
    // renglones no es "no incluye nada". Sin la clave (un backend que no la manda) no se
    // afirma nada sobre las inclusiones: quedan las de props.
    savedTicketType = (ticketType, sentInclusions, previous) => {
        const out = { ...ticketType };
        if (!('inclusions' in out) || out.inclusions === undefined) {
            delete out.inclusions;
            return out;
        }
        const expected = sentInclusions || (Array.isArray(previous) ? previous : null);
        if (!Array.isArray(out.inclusions) || (expected && out.inclusions.length !== expected.length)) {
            out.inclusions = null;
        }
        // El tipo se editó con la lectura fallida (`previous === null`), así que la lista no
        // viajó. Si la respuesta vuelve vacía no se sabe si es que no incluye nada o que la
        // lectura sigue fallando (el backend devuelve `[]` en los dos casos): sigue sin leer.
        // Una lista con renglones sí es una lectura buena.
        if (!sentInclusions && previous === null && Array.isArray(out.inclusions) && out.inclusions.length === 0) {
            out.inclusions = null;
        }
        return out;
    }

    // Después de guardar se le pide al padre que vuelva a leer el evento. Si avisa que pudo
    // (`onRefresh` devuelve una promesa que resuelve en algo distinto de false), props ya trae
    // lo guardado y lo que se retuvo hasta ese guardado se suelta. Si falla, queda.
    refreshAfterSave = (seq) => {
        if (typeof this.props.onRefresh !== 'function') return;
        Promise.resolve(this.props.onRefresh())
            .then((ok) => {
                if (ok === false || this.unmounted) return;
                this.setState((prev) => {
                    const saved = {};
                    Object.keys(prev.saved || {}).forEach((id) => {
                        if (prev.saved[id].seq > seq) saved[id] = prev.saved[id];
                    });
                    return { saved };
                });
            })
            .catch(() => { /* el refresco falló: lo guardado sigue a la vista */ });
    }

    componentDidMount() {
        if (this.anyInclusions()) this.fetchMenu();
    }

    componentDidUpdate() {
        if (this.anyInclusions()) this.fetchMenu();
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    anyInclusions = () => this.ticketTypes().some((tt) => (tt.inclusions || []).length > 0);

    // Algún tipo con la lectura de "Qué incluye" fallida (`inclusions: null`).
    anyUnreadable = () => this.ticketTypes().some((tt) => tt.inclusions === null);

    // El menú se pide UNA vez, y recién cuando hace falta: si algún tipo ya declara qué incluye o
    // cuando se agrega el primer renglón. Un evento sin inclusiones no hace ningún pedido nuevo.
    // Si falla no se avisa con un alert ni se redirige: el editor lo dice en su lugar y el resto
    // del formulario sigue andando.
    //
    // Es el menú ENTERO (`cafeteria/menu`: todo lo que no está eliminado, visible o no) y no el
    // catálogo del POS: es la misma vara con la que el backend valida un renglón por ítems, así
    // que un código guardado que no está en esta lista es un ítem dado de baja, y el editor lo
    // puede decir.
    fetchMenu = () => {
        if (this.menuRequested) return;
        this.menuRequested = true;
        const request = {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        };
        const failed = () => { if (!this.unmounted) this.setState({ menu: [], menuError: true }); };
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}cafeteria/menu`, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) return failed();
                if (!this.unmounted) this.setState({ menu: data.data || [] });
            })
            .catch((error) => {
                console.error('No se pudo cargar el menú de cafetería:', error);
                failed();
            });
    }

    onChangeInclusions = (inclusions) => {
        if (this.state.inclusionsUnreadable) return;
        this.setState({ inclusions, sendInclusions: true });
        if (inclusions.length > 0) this.fetchMenu();
    }

    handleChange = (e) => {
        const { name, type, checked, value } = e.target;
        this.setState({ [name]: type === 'checkbox' ? checked : value });
    }

    toLocalInput = (value) => (value ? String(value).slice(0, 16) : '');

    startEdit = (tt) => {
        this.setState({
            editingId: tt.id,
            name: tt.name || '',
            price: tt.price != null ? tt.price : '',
            kind: tt.kind || 'general',
            validFrom: this.toLocalInput(tt.valid_from),
            validUntil: this.toLocalInput(tt.valid_until),
            active: tt.active != null ? !!tt.active : true,
            disabled: false,
            inclusions: rowsFromInclusions(tt.inclusions),
            // Hasta que no se toque el editor la lista no viaja: "sin la clave" es "no tocar"
            // (FR-004). Si viajara siempre, un renglón guardado con un ítem que después se dio
            // de baja haría rechazar (7607) hasta un cambio de precio.
            sendInclusions: false,
            inclusionsUnreadable: tt.inclusions === null,
        });
    }

    cancelEdit = () => this.setState(this.emptyForm());

    onSubmit = () => {
        if (this.state.name.trim() === '' || String(this.state.price).trim() === '') {
            alert('Completá nombre y precio del tipo de entrada.');
            return;
        }
        // parseInt le cortaba los centavos en silencio (350,5 → 350) y el tipo se creaba con otro
        // precio. Se rechaza igual que el backend y el formulario de primera jornada.
        const precio = Number(String(this.state.price).trim().replace(',', '.'));
        if (!Number.isInteger(precio) || precio < 0) {
            alert('El precio va en pesos enteros, sin centavos.');
            return;
        }
        // Con la lectura fallida la lista no viaja pase lo que pase (el editor ya está
        // deshabilitado; esto es el cinturón).
        const sendInclusions = this.state.sendInclusions && !this.state.inclusionsUnreadable;
        if (sendInclusions) {
            const problema = inclusionsError(this.state.inclusions);
            if (problema) {
                alert(`Qué incluye: ${problema}`);
                return;
            }
        }
        this.setState({ disabled: true });
        const editing = this.state.editingId != null;
        const body = {
            name: this.state.name.trim(),
            price: precio,
            kind: this.state.kind || 'general',
        };
        body.validFrom = String(this.state.validFrom).trim() !== '' ? this.state.validFrom : null;
        body.validUntil = String(this.state.validUntil).trim() !== '' ? this.state.validUntil : null;
        if (editing) body.active = !!this.state.active;
        // La lista completa y en orden: el backend reemplaza la del tipo por ésta (FR-003), y
        // vacía la borra (FR-005).
        if (sendInclusions) body.inclusions = inclusionsPayload(this.state.inclusions);
        const editingId = this.state.editingId;
        // Lo que el tipo tenía antes de este guardado: su lista, null si no se pudo leer, o
        // undefined si es un tipo nuevo (o uno de un evento que no declara nada).
        const editedType = editing ? this.ticketTypes().find((tt) => tt.id === editingId) : null;
        const previous = editedType ? editedType.inclusions : undefined;

        const base = `${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}manage-events/${this.props.eventId}/ticket-types`;
        const url = editing ? `${base}/${this.state.editingId}` : base;
        const request = {
            method: editing ? 'PATCH' : 'POST',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        };
        fetch(url, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    // ENI-116: el mensaje de negocio (precio/tipo inválido, etc.) viene anidado en data.data.message.
                    // El rechazo de "Qué incluye" (7607) trae su detalle ya armado en data.message.
                    alert((data.data && data.data.message) || data.message);
                    this.setState({ disabled: false });
                    if (data.error == 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                } else {
                    // La respuesta trae el tipo como quedó guardado: es lo que se muestra y de
                    // donde parte la próxima edición, llegue o no el refresco.
                    const returned = (data.data && data.data.ticketType) || {};
                    const id = returned.id != null ? returned.id : editingId;
                    const seq = ++this.saveSeq;
                    this.setState((prev) => ({
                        ...this.emptyForm(),
                        saved: id == null ? prev.saved : {
                            ...prev.saved,
                            [id]: {
                                seq,
                                ticketType: this.savedTicketType({ ...returned, id }, body.inclusions, previous),
                            },
                        },
                    }));
                    this.refreshAfterSave(seq);
                }
            })
            .catch((error) => {
                console.error('No se pudo guardar el tipo de entrada:', error);
                alert('Error de conexión');
                this.setState({ disabled: false });
            });
    }

    kindLabel = (kind) => {
        const o = KIND_OPTIONS.find((k) => k.value === kind);
        return o ? o.label : (kind || '-');
    }

    render() {
        const ticketTypes = this.ticketTypes();
        const editing = this.state.editingId != null;
        // La columna aparece sólo si algún tipo declara algo: un evento sin inclusiones ve la
        // misma tabla de siempre.
        const showIncluded = this.anyInclusions() || this.anyUnreadable();
        const menuByCode = menuByCodeOf(this.state.menu);
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">Tipos de entrada</h6>
                </div>
                <div className="card-body p-0">
                    <table className="table mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Nombre</th>
                                <th>Precio</th>
                                <th>Tipo</th>
                                <th>Válido desde</th>
                                <th>Válido hasta</th>
                                <th>Activo</th>
                                {showIncluded && <th>Incluye</th>}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {ticketTypes.length === 0 && (
                                <tr><td colSpan={showIncluded ? 8 : 7} className="text-center text-muted">Todavía no hay tipos de entrada.</td></tr>
                            )}
                            {ticketTypes.map((tt) => (
                                <tr key={tt.id} style={{ background: this.state.editingId === tt.id ? '#fff8e1' : undefined }}>
                                    <td>{tt.name}</td>
                                    <td>$ {tt.price}</td>
                                    <td>{this.kindLabel(tt.kind)}</td>
                                    <td>{formatEventDateTime(tt.valid_from)}</td>
                                    <td>{formatEventDateTime(tt.valid_until)}</td>
                                    <td>{tt.active ? 'Sí' : 'No'}</td>
                                    {showIncluded && (
                                        <td>
                                            {tt.inclusions === null && (
                                                <small className="d-block text-danger event-inclusions-unreadable">No se pudo leer qué incluye esta entrada.</small>
                                            )}
                                            {tt.inclusions !== null && ((tt.inclusions || []).length === 0 ? '—' : (tt.inclusions || []).map((inc, i) => (
                                                <small key={inc.id != null ? inc.id : i} className="d-block">{describeInclusion(inc, menuByCode)}</small>
                                            )))}
                                        </td>
                                    )}
                                    <td className="text-right">
                                        <button type="button" className="btn btn-sm btn-white" onClick={() => this.startEdit(tt)}>Editar</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="card-body border-top">
                    <h6 className="text-uppercase text-muted">{editing ? 'Editar tipo de entrada' : 'Nuevo tipo de entrada'}</h6>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Nombre *</label>
                                <input type="text" name="name" className="form-control" value={this.state.name} onChange={this.handleChange} />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Precio *</label>
                                <input type="number" name="price" className="form-control" value={this.state.price} onChange={this.handleChange} />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Tipo</label>
                                <select name="kind" className="form-control" value={this.state.kind} onChange={this.handleChange}>
                                    {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Válido desde</label>
                                <input type="datetime-local" name="validFrom" className="form-control" value={this.state.validFrom} onChange={this.handleChange} />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Válido hasta</label>
                                <input type="datetime-local" name="validUntil" className="form-control" value={this.state.validUntil} onChange={this.handleChange} />
                            </div>
                        </div>
                        {editing && (
                            <div className="col-md-1">
                                <div className="form-group">
                                    <label className="d-block text-nowrap">Activo</label>
                                    <input type="checkbox" name="active" checked={this.state.active} onChange={this.handleChange} />
                                </div>
                            </div>
                        )}
                    </div>
                    <EventInclusionsEditor
                        value={this.state.inclusions}
                        onChange={this.onChangeInclusions}
                        menu={this.state.menu}
                        menuError={this.state.menuError}
                        disabled={this.state.disabled}
                        unreadable={this.state.inclusionsUnreadable}
                    />
                    <button type="button" className="btn btn-accent" disabled={this.state.disabled} onClick={this.onSubmit}>
                        {editing ? 'Guardar cambios' : 'Agregar tipo'}
                    </button>
                    {editing && (
                        <button type="button" className="btn btn-outline-secondary ml-2" disabled={this.state.disabled} onClick={this.cancelEdit}>
                            Cancelar
                        </button>
                    )}
                </div>
            </div>
        );
    }
}

export default EventTicketTypesComponent;
